/* eslint-disable */
import { aclHelper, isJson } from 'helper';
import { startCase, capitalize, get, upperCase } from 'lodash';
import Badges from 'modules/common/components/Badge';
import { useShowSubscriber } from 'modules/for-verification/hooks';
import Button from 'partial/components/Button';
import PropTypes from 'prop-types';
import ThumbIcon from 'partial/components/ThumbIcon';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { HiDocumentText } from 'react-icons/hi';
import Card from 'partial/components/Card';
import classNames from 'classnames';
import empty from 'assets/images/icons/empty-list.svg';
import { useOpenApprovalModal } from '../modals/ApprovalModal';

export const NoReqs = () => {
  return (
    <div className="h-full flex flex-col justify-center items-center gap-2">
      <img src={empty} alt="empty" />
      <div className="font-semibold text-sm tracking-wide text-gray-600">
        This user has not submitted any documents yet.
      </div>
    </div>
  );
};

export const Requirement = ({ req }) => {
  if (req.type.includes('pdf'))
    return (
      <button type="button" className="w-full p-6">
        <a
          href={req.url}
          target="_blank"
          rel="noreferrer noopener"
          className="flex flex-col gap-2 items-center justify-center drop-shadow w-fit"
        >
          <HiDocumentText className="w-10 h-10 text-primary" />
          <div className="text-gray-600 font-semibold">{req?.name}</div>
        </a>
      </button>
    );
  return <img src={req.url} alt={req.name} className="max-h-[300px]" />;
};

Requirement.propTypes = {
  req: PropTypes.instanceOf(Object).isRequired,
};

function VerificationDetails({ callBack }) {
  const { id } = useParams();
  const openApprovalModal = useOpenApprovalModal();
  const [isLoading, subscriber] = useShowSubscriber(id);
  const profile = get(subscriber, 'profile');
  const organization = get(subscriber, 'organization');
  const requirementsRaw = get(organization, 'requirements');
  const subscriberName = startCase(
    capitalize(`${profile?.first_name} ${profile?.last_name}`),
  );

  const requirements = useMemo(
    () => ({
      ...requirementsRaw,
      government_id: isJson(requirementsRaw?.government_id)
        ? JSON.parse(requirementsRaw?.government_id)
        : requirementsRaw?.government_id,
      secondary_government_id: isJson(requirementsRaw?.secondary_government_id)
        ? JSON.parse(requirementsRaw?.secondary_government_id)
        : requirementsRaw?.secondary_government_id,
    }),
    [requirementsRaw],
  );

  const renderActions = useMemo(() => {
    if (requirements?.status === 'Pending')
      return (
        <div className="space-y-2">
          {aclHelper(
            'subscriber_requirement',
            'u',
            <>
              {' '}
              <Button
                size="xs"
                primary
                className="w-[150px] uppercase"
                onClick={() =>
                  openApprovalModal({
                    type: 'approve',
                    organization,
                    cb: callBack,
                  })
                }
              >
                Approve
              </Button>
              <Button
                size="xs"
                outline
                className="w-[150px] uppercase"
                onClick={() =>
                  openApprovalModal({
                    type: 'reject',
                    organization,
                    cb: callBack,
                  })
                }
              >
                Disapprove
              </Button>
            </>,
          )}
        </div>
      );
    return (
      <div>
        {subscriber?.status !== 'No Requirements Passed.' && (
          <Badges
            status={upperCase(subscriber?.status)}
            containerClassName="!text-sm !px-8 w-fit"
          />
        )}
      </div>
    );
  }, [requirements, subscriber, organization, callBack]);

  return (
    <div className="w-full px-4">
      {isLoading ? (
        <div className="grid grid-cols-2 gap-3 p-3">
          <div className="animate-pulse bg-gray-50 h-8 w-full rounded" />
          <div className="animate-pulse bg-gray-50 h-8 w-full rounded" />
          <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
          <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
          <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
        </div>
      ) : (
        <div className="flex flex-col gap-2 h-full">
          <div className="px-4 py-6 flex justify-between border-b border-gray-200/50">
            <div className="flex gap-8">
              <ThumbIcon
                className="h-24 w-24 rounded-full"
                initialsClassName="text-5xl"
                alt={subscriberName}
                url={profile?.avatar || ''}
              />
              <div className="space-y-px">
                <div className="font-bold text-lg">{subscriberName}</div>
                <div className="text-sm">{organization?.name}</div>
                <div className="text-sm">{subscriber?.email}</div>
                <div className="text-sm">{profile?.mobile_number}</div>
                <div className="pt-2">
                  {requirements?.remarks && (
                    <div className={classNames('flex flex-col gap-px text-sm')}>
                      <div className="font-bold">Remarks:</div>
                      <div>{requirements?.remarks}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>{renderActions}</div>
          </div>
          <Card className="mt-5 px-6 py-5 flex-1">
            {requirementsRaw ? (
              <div className="space-y-5">
                <div>
                  <label className="text-sm text-gray-800/60">
                    Government ID:
                  </label>
                  <div className="rounded mt-1 p-3 border border-gray-100/60 w-full">
                    <Requirement req={requirements?.government_id} />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-gray-800/60">
                    Secondary Government ID:
                  </label>
                  <div className="rounded mt-1 p-3 border border-gray-100/60 w-full">
                    <Requirement req={requirements?.secondary_government_id} />
                  </div>
                </div>
              </div>
            ) : (
              <NoReqs />
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

VerificationDetails.propTypes = {
  callBack: PropTypes.func.isRequired,
};

export default VerificationDetails;
